/**
 * Builds a human-readable survey display name from its component parts.
 *
 * The format varies by program type:
 *   - DHS:  "DHS <countryName> <year>"
 *   - SPA:  "SPA <countryName> <year>"
 *   - LSMS: "LSMS <countryName> <year>"
 *   - MICS: "MICS <countryName> <year> <round>"
 *
 * @param programName - The survey program identifier (DHS, MICS, LSMS, SPA).
 * @param countryName - The full country name to display.
 * @param surveyYear - The survey year.
 * @param round - The MICS round identifier (only used for MICS).
 * @returns The formatted survey display name.
 */
export function buildSurveyName(
  programName: string,
  countryName: string,
  surveyYear: string,
  round?: string
): string {
  const base = `${programName} ${countryName} ${surveyYear}`
  if (programName === "MICS" && round) {
    return `${base} ${round}`
  }
  return base
}

/**
 * DHS/SPA data file type codes mapped to human-readable unit of analysis labels.
 * Source: https://dhsprogram.com/data/File-Types-and-Names.cfm
 *
 * These codes appear as the 3rd-4th characters of a DHS/SPA file name
 * (e.g. "IR" in "KEIR71FL.SAV") and are stored in the `recode` column of the
 * DHS/SPA variables table.
 */
const DHS_SPA_RECODE_MAP: Record<string, string> = {
  // Household survey recodes
  AI: "Accidents and Injuries",
  BR: "Births",
  CR: "Couples",
  GR: "Pregnancies",
  HR: "Household",
  IR: "Individual (Women)",
  KR: "Children Under 5",
  MR: "Men",
  NR: "Pregnancy and Postnatal Care",
  PR: "Household Members",
  SR: "Siblings",
  XR: "Child Under 5",
  // Household survey raw
  AH: "Adult Health",
  BQ: "Births Raw",
  CH: "Children Raw",
  CP: "Couples Raw",
  EX: "Experimental",
  FW: "Fieldworker",
  HH: "Household Raw",
  HW: "Height and Weight (WHO)",
  ID: "In-depth",
  IH: "Individual/Household Raw",
  IQ: "Individual Raw",
  ML: "Men Raw",
  OD: "Other Data",
  PQ: "Household Member Raw",
  SM: "Safe Motherhood",
  SQ: "Service Availability Raw",
  VA: "Verbal Autopsy",
  WI: "Wealth Index",
  WS: "Women's Status",
  XP: "Expenditure",
  // HIV/Biomarkers
  AR: "HIV Test Results",
  HT: "HIV Test Results Raw",
  OB: "Other Biomarkers",
  // GIS
  GC: "Geospatial Covariates",
  GE: "Geographic Data",
  // Facility survey / SPA
  AN: "Antenatal Care",
  AT: "ART",
  CL: "Unit Check List",
  CN: "Consultations",
  CS: "Country Specific",
  CT: "VCT",
  FC: "Facility",
  FP: "Family Planning",
  IN: "Safe Injection",
  IP: "Inpatient Unit",
  LB: "Laboratory",
  LD: "Labor Delivery",
  MS: "Health Information System",
  OI: "Outpatient/Inpatient",
  OP: "Outpatient Unit",
  PH: "Pharmacy",
  PI: "Personal Interview",
  PM: "PMTCT",
  PV: "Provider",
  SC: "Sick Child",
  SI: "STI",
  SL: "Staff/Provider Listing",
  TB: "TB Data",
  // Community
  CO: "Community",
  VR: "Village",
}

/**
 * MICS unit of analysis codes mapped to human-readable labels.
 * The `unitofanalysis` column in the MICS variables table stores these codes.
 */
const MICS_UNIT_MAP: Record<string, string> = {
  BH: "Birth History",
  CH: "Children Under 5",
  FG: "Female Genital Mutilation",
  FS: "Children 5-17",
  HL: "Household Members",
  HH: "Household",
  MN: "Men",
  TN: "Mosquito Nets in Household",
  WM: "Women",
  WQ: "Water Quality",
}

/**
 * Resolves a unit of analysis code to a human-readable label based on the
 * survey program type.
 *
 * @param code - The raw unit of analysis code (recode for DHS/SPA, unitofanalysis for LSMS).
 * @param programName - The survey program ("DHS", "SPA", or "LSMS").
 * @returns The human-readable label, or the raw code if no mapping is found.
 */
export function getUnitOfAnalysisLabel(code: string, programName: string): string {
  if (!code) return ""

  const upperCode = code.toUpperCase()

  if (programName === "MICS") {
    return MICS_UNIT_MAP[upperCode] ?? code
  }

  // DHS and SPA use the same recode mapping
  if (programName === "DHS" || programName === "SPA") {
    return DHS_SPA_RECODE_MAP[upperCode] ?? code
  }

  // LSMS and unknown programs — return the raw code
  return code
}
